// ─────────────────────────────────────────────────────────────────────────────
// LogoutConfirmationModal Component - TypeScript Implementation
// Professional logout confirmation modal with modern UI
// ─────────────────────────────────────────────────────────────────────────────

import React from "react";
import "./LogoutConfirmationModal.css";

interface LogoutConfirmationModalProps {
  isOpen: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  userName?: string;
}

const LogoutConfirmationModal: React.FC<LogoutConfirmationModalProps> = ({
  isOpen,
  onConfirm,
  onCancel,
  userName = "User",
}) => {
  if (!isOpen) return null;

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onCancel();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      onCancel();
    } else if (e.key === "Enter") {
      onConfirm();
    }
  };

  return (
    <div 
      className="logout-modal-backdrop" 
      onClick={handleBackdropClick}
      onKeyDown={handleKeyDown}
      role="dialog"
      aria-modal="true"
      aria-labelledby="logout-modal-title"
      tabIndex={-1}
    >
      <div className="logout-modal-container">
        <div className="logout-modal-header">
          <div className="logout-modal-icon">
            <span role="img" aria-label="Logout icon">🚪</span>
          </div>
          <h2 id="logout-modal-title" className="logout-modal-title">
            Confirm Logout
          </h2>
          <button
            className="logout-modal-close"
            onClick={onCancel}
            aria-label="Close modal"
            title="Close"
          >
            ×
          </button>
        </div>
        
        <div className="logout-modal-body">
          <p className="logout-modal-message">
            Are you sure you want to logout, <strong>{userName}</strong>?
          </p>
          <p className="logout-modal-submessage">
            You will need to sign in again to access the application.
          </p>
        </div>
        
        <div className="logout-modal-footer">
          <button
            className="logout-modal-btn logout-modal-btn-cancel"
            onClick={onCancel}
            type="button"
          >
            Cancel
          </button>
          <button
            className="logout-modal-btn logout-modal-btn-confirm"
            onClick={onConfirm}
            type="button"
            autoFocus
          >
            Logout
          </button>
        </div>
      </div>
    </div>
  );
};

export default LogoutConfirmationModal;
export type { LogoutConfirmationModalProps };
